import { useState, useEffect } from 'react'
import { useSearchParams, useParams, Link } from 'react-router-dom'
import { PageHero, CtaSection } from '../../components'
import { Loader } from '../../components/ui'
import { getApiBase } from '../../features/projects/api'
import useDocumentTitle from '../../hooks/useDocumentTitle'
import './ProjectDetail.css'

interface SpecItem {
  key: string
  value: string
}

interface DetailData {
  title: string
  specs: SpecItem[]
  sliderImages: string[]
  content: string
  url?: string
}

const ProjectDetailPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const projectUrl = searchParams.get('url')
  
  const [data, setData] = useState<DetailData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [activeImgIndex, setActiveImgIndex] = useState<number>(0)

  const [backPath, setBackPath] = useState('/cong-trinh-thiet-ke')
  const [backName, setBackName] = useState('Công trình thiết kế')

  useEffect(() => {
    try {
      const savedPath = sessionStorage.getItem('lastVisitedList')
      const savedName = sessionStorage.getItem('lastVisitedListName')
      if (savedPath && savedName) {
        setBackPath(savedPath)
        setBackName(savedName)
      } else {
        const resolvedUrl = projectUrl || data?.url || ''
        const isArticle = resolvedUrl.includes('maxhomegroup.vn')
        if (isArticle) {
          setBackPath('/kien-thuc')
          setBackName('Kiến thức')
        } else {
          setBackPath('/cong-trinh-thiet-ke')
          setBackName('Công trình thiết kế')
        }
      }
    } catch (e) {
      // ignore
    }
  }, [projectUrl, data])

  useEffect(() => {
    window.scrollTo(0, 0)

    if (!slug && !projectUrl) {
      setError('Đường dẫn công trình không hợp lệ hoặc không tồn tại.')
      setLoading(false)
      return
    }

    const fetchDetail = async () => {
      setLoading(true)
      setError(null)
      try {
        const queryParam = slug ? `slug=${slug}` : `url=${encodeURIComponent(projectUrl || '')}`
        const response = await fetch(`${getApiBase()}/api/project/detail?${queryParam}`)
        const json = await response.json()
        
        if (json.success && json.data) {
          setData(json.data)
          setActiveImgIndex(0)
        } else {
          setError(json.message || 'Không thể lấy thông tin chi tiết công trình.')
        }
      } catch (err: any) {
        console.error(err)
        setError('Kết nối đến máy chủ thất bại. Vui lòng đảm bảo backend đang chạy.')
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [slug, projectUrl])

  // Trích xuất mã số từ tiêu đề (ví dụ: "... | MH06104")
  const getProjectCode = () => {
    if (!data?.title) return ''
    const parts = data.title.split('|')
    return parts[1] ? parts[1].trim() : ''
  }

  // Tiêu đề hiển thị không có mã số ở đuôi
  const getDisplayTitle = () => {
    if (!data?.title) return ''
    const parts = data.title.split('|')
    return parts[0].trim()
  }

  useDocumentTitle(data?.title ? `${getDisplayTitle()} - MAXCONST` : 'Chi tiết - MAXCONST')

  const handlePrevImage = () => {
    if (!data?.sliderImages || data.sliderImages.length === 0) return
    setActiveImgIndex((prev) => (prev === 0 ? data.sliderImages.length - 1 : prev - 1))
  }

  const handleNextImage = () => {
    if (!data?.sliderImages || data.sliderImages.length === 0) return
    setActiveImgIndex((prev) => (prev === data.sliderImages.length - 1 ? 0 : prev + 1))
  }

  if (loading) {
    return (
      <div className="project-detail-loading">
        <Loader text="Đang cào dữ liệu chi tiết công trình..." />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="container section text-center" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--color-warning)', marginBottom: '1.5rem' }} />
        <h3 style={{ marginBottom: '1rem', color: 'var(--color-text)' }}>Gặp lỗi khi tải trang chi tiết</h3>
        <p style={{ color: 'var(--color-text-secondary)', maxWidth: '500px', marginBottom: '2rem' }}>{error}</p>
        <Link to={backPath} className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Quay lại danh sách
        </Link>
      </div>
    )
  }

  const resolvedUrl = projectUrl || data?.url || ''
  const isArticle = resolvedUrl.includes('maxhomegroup.vn')

  const breadcrumbs = [
    { label: 'Trang chủ', path: '/' },
    { label: backName, path: backPath },
    { label: isArticle ? 'Chi tiết bài viết' : 'Chi tiết công trình' }
  ]

  const projectCode = getProjectCode()
  const displayTitle = getDisplayTitle()
  const hasSlider = data.sliderImages && data.sliderImages.length > 0
  const hasSpecs = data.specs && data.specs.length > 0

  return (
    <div className="project-detail-page">
      {/* Hero */}
      <PageHero
        tag={isArticle ? 'Kiến thức & Kinh nghiệm' : (projectCode ? `Mã công trình: ${projectCode}` : 'Chi tiết công trình')}
        title={displayTitle}
        description={isArticle ? 'Bài viết chia sẻ kinh nghiệm xây dựng, cẩm nang nhà đẹp từ kiến trúc sư Maxconst.' : `Bản vẽ chi tiết thiết kế công trình ${projectCode || ''} chuẩn công năng, tối ưu chi phí.`}
        breadcrumbs={breadcrumbs}
      />

      <div className="container section">
        
        {/* TOP SECTION: SLIDER & SPECS TABLE */}
        {(hasSlider || hasSpecs) && (
          <div className="project-detail-top-card">
            <div className="project-detail-top-grid">
              
              {/* SLIDER CỘT TRÁI */}
              <div className="detail-slider-container">
                {hasSlider ? (
                  <div className="detail-slider-wrapper">
                    <img 
                      src={data.sliderImages[activeImgIndex]} 
                      alt={`${displayTitle} - Slide ${activeImgIndex + 1}`} 
                      className="detail-slider-image"
                      referrerPolicy="no-referrer"
                    />
                    
                    {data.sliderImages.length > 1 && (
                      <>
                        {/* Nút điều hướng */}
                        <button className="slider-arrow arrow-left" onClick={handlePrevImage} aria-label="Previous image">
                          <i className="fas fa-chevron-left" />
                        </button>
                        <button className="slider-arrow arrow-right" onClick={handleNextImage} aria-label="Next image">
                          <i className="fas fa-chevron-right" />
                        </button>
                        
                        {/* Chỉ số chấm tròn */}
                        <div className="slider-indicators">
                          {data.sliderImages.map((_, index) => (
                            <button
                              key={index}
                              className={`indicator-dot ${index === activeImgIndex ? 'active' : ''}`}
                              onClick={() => setActiveImgIndex(index)}
                              aria-label={`Go to slide ${index + 1}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="slider-placeholder">
                    🏡 Không có hình ảnh trượt
                  </div>
                )}
              </div>
              
              {/* BẢNG THÔNG SỐ CỘT PHẢI */}
              <div className="detail-specs-container">
                <div className="specs-table-card">
                  <h3 className="specs-table-title">Thông số thiết kế</h3>
                  {hasSpecs ? (
                    <div className="table-responsive">
                      <table className="specs-data-table">
                        <tbody>
                          {data.specs.map((spec, index) => (
                            <tr key={index}>
                              <th className="spec-key">{spec.key}:</th>
                              <td className="spec-value">{spec.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="no-specs-text">Đang cập nhật thông số kĩ thuật...</p>
                  )}
                  
                  {/* Nút tư vấn điện thoại trực tiếp */}
                  <a href="tel:0979808278" className="specs-consult-btn">
                    <i className="fas fa-phone-alt animate-pulse" />
                    Tư vấn: <strong>0979 808 278</strong>
                  </a>
                </div>
              </div>
              
            </div>
          </div>
        )}

        {/* SECTION TIÊU ĐỀ THÔNG TIN CHI TIẾT */}
        <div className="detail-section-header">
          <span className="section-title-bar"></span>
          <h2 className="detail-section-title">THÔNG TIN CHI TIẾT</h2>
        </div>

        {/* BOTTOM SECTION: DETAILED DESCRIPTION & SIDEBAR */}
        <div className="project-detail-layout">
          {/* Cột trái: Nội dung mô tả chi tiết */}
          <main className="project-detail-main">
            <div className="project-detail-content-wrap">
              <div 
                className="project-detail-rich-text wp-content"
                dangerouslySetInnerHTML={{ __html: data.content }}
              />
            </div>
          </main>

          {/* Cột phải: Sidebar hỗ trợ khách hàng */}
          <aside className="project-detail-sidebar">
            <div className="sidebar-card sticky-sidebar">
              <h3 className="sidebar-title">Đăng ký tư vấn</h3>
              <p className="sidebar-desc">
                Nhập thông tin liên hệ của bạn để kiến trúc sư Maxconst trực tiếp gọi điện giải đáp mọi thắc mắc của bạn về thiết kế này.
              </p>
              
              <div className="sidebar-info-list">
                {!isArticle && projectCode && (
                  <div className="sidebar-info-item">
                    <span className="info-label">Mã số thiết kế:</span>
                    <span className="info-value highlight-value">{projectCode}</span>
                  </div>
                )}
                {!isArticle && (
                  <div className="sidebar-info-item">
                    <span className="info-label">Trạng thái:</span>
                    <span className="info-value status-available">Sẵn sàng bàn giao</span>
                  </div>
                )}
                <div className="sidebar-info-item">
                  <span className="info-label">{isArticle ? 'Đơn vị tư vấn:' : 'Đơn vị thiết kế:'}</span>
                  <span className="info-value">MAXCONST Co.</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="sidebar-actions">
                <a href="tel:0979808278" className="sidebar-btn btn-call">
                  <i className="fas fa-phone-alt" />
                  Gọi điện: 0979 808 278
                </a>
                <a 
                  href={`https://zalo.me/0979808278`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="sidebar-btn btn-zalo"
                >
                  <i className="fab fa-whatsapp" /> {/* Zalo chat icon */}
                  Nhắn tin Zalo tư vấn
                </a>
              </div>

              {/* Cam kết */}
              <div className="sidebar-guarantee">
                <div className="guarantee-item">
                  <i className="fas fa-check-circle" />
                  <span>Hồ sơ thiết kế kỹ thuật thi công đầy đủ</span>
                </div>
                <div className="guarantee-item">
                  <i className="fas fa-check-circle" />
                  <span>Dự toán kinh phí xây dựng chuẩn xác 95%</span>
                </div>
                <div className="guarantee-item">
                  <i className="fas fa-check-circle" />
                  <span>Bản vẽ chuẩn phong thủy gia chủ</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* CTA Section */}
      <CtaSection
        title="Nhận Báo Giá Thiết Kế Và Thi Công Trọn Gói"
        titleHighlight="Báo Giá"
        description="Đăng ký thông tin để kiến trúc sư Maxconst hỗ trợ khảo sát mặt bằng và lên phương án mặt bằng sơ bộ miễn phí."
      />
    </div>
  )
}

export default ProjectDetailPage
